// Added by Jonghyung Lee on 2021-02-16
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root Store
import RootStore from '../../index'

// Services
import {
  reqGetNotifications,
  reqGetNotification,
  reqUpdateNotification,
  reqUpdateNotifications,
  reqDeleteNotification,
  reqDeleteNotifications,
  reqCheckUrl,
} from '@/service/notifications.service'

import { NotificationItem, ReqGetNotifications } from '@/helper/define'
import Logger from '@/helper/Logger'
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class NotificationStore {
  elements: NotificationItem[] = []
  unreadElements: NotificationItem[] = []
  hasNextPage: boolean = false
  root?: RootStore
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: RootStore) {
    this.root = root
    makeAutoObservable(this)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElements = async (path: ReqGetNotifications, isPopup: boolean = false) => {
    const {
      data: { items, next_page_index },
    } = await reqGetNotifications(path)
    switch (next_page_index) {
      case '-1': 
        this.hasNextPage = false
        break
      default: 
        this.hasNextPage = true
        break
    }

    if (isPopup) this._setPopupElements(items!)
    else this._setElements(items!)
    return this.elements
  }

  getPopupElements = async (path: ReqGetNotifications) => {
    const {
      data: { items },
    } = await reqGetNotifications(path)

    this._setPopupElements(items!)
  }

  getElement = async (id: string) => {
    return await reqGetNotification(id)
  }

  updateElement = async (id: string, element: NotificationItem) => {
    try {
      const { data: updatedElement } = await reqUpdateNotification(id, element)
      const updatedElements = this.elements.map(element => {
        if (element.id === updatedElement.id) return updatedElement
        return element
      })
      this._setElements(updatedElements)
    } catch (error) {
      Logger.errLog(`[NotificationStore] updateElement Failed: ${error}`)
    }
  }

  /** 전부 Update 시켰더라도 기존의 Query만큼만 보여줘야 하므로 외부에서 다시 Get 호출 */
  updateElements = async (userId: string) => {
    try {
      await reqUpdateNotifications(userId)
    } catch (error) {
      Logger.errLog(`[NotificationStore] updateElements Failed: ${error}`)
    }
  }

  /** 항목을 Delete 시키고 기존의 Query만큼을 다시 보여줘야 하므로 외부에서 다시 Get 호출 */
  deleteElement = async (id: string) => {
    try {
      await reqDeleteNotification(id)
    } catch (error) {
      Logger.errLog(`[NotificationStore] deleteElement Failed: ${error}`)
    }
    return
  }

  deleteElements = async (userId: string) => {
    try {
      await reqDeleteNotifications(userId)
      this._setElements([])
      this.hasNextPage = false
    } catch (error) {
      Logger.errLog(`[NotificationStore] deleteElements Failed: ${error}`)
    }
  }

  checkElementUrl = async (id: string, url: string) => {
    try {
      const { data : { is_valid_url } } = await reqCheckUrl(id, url)
      return is_valid_url
    } catch (error) {
      Logger.errLog(`[NotificationStore] checkElementUrl Failed: ${error}`)
    }
  }

  clearElements = () => {
    /** Logout 시에 모든 Element를 초기화한다 */
    this.unreadElements = []
    this.elements = []
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setElements = (elements: NotificationItem[]) => {
    this.elements = elements
  }

  _setPopupElements = (elements: NotificationItem[]) => {
    this.unreadElements = elements
  }

  get getNotifications() {
    return this.elements
  }
}
